import React, { useState } from "react";
import { PlusOutlined } from "@ant-design/icons";
import { ArchiveBoxIcon } from "../../../../assets/icons/ArchiveBoxIcon";
import WorkspaceCard from "./WorkspaceCard";
import WorkspaceFormModal from "./WorkspaceFormModal";

import "./workspaceSelection.scss";

const SKELETON_CARDS = [1, 2, 3, 4, 5, 6, 7, 8];

function WorkspaceSelection({
   userName,
   roleLabel,
   workspaces = [],
   isLoading = false,
   onEnterWorkspace,
   canCreateWorkspace = false,
   renderWorkspaceActions,
   onWorkspaceSaved,
   // Opens the archived workspaces, when they may be seen (components/Archive).
   onShowArchived
}) {
   // `null` when closed, `{ workspace }` when open — `workspace` is undefined for "add".
   const [formTarget, setFormTarget] = useState(null);
   const hasWorkspaces = workspaces.length > 0;

   const closeForm = () => setFormTarget(null);

   const handleSaved = (workspace) => {
      closeForm();
      onWorkspaceSaved?.(workspace);
   };

   return (
      <div className="workspace-selection">
         <header className="workspace-selection__hero">
            {roleLabel && <span className="workspace-selection__role">{roleLabel}</span>}
            <h1 className="workspace-selection__greeting">
               Welcome back, <span>{userName}!</span>
            </h1>
         </header>

         <section className="workspace-selection__panel">
            <div className="workspace-selection__panel-header">
               <div className="workspace-selection__heading">
                  <h2 className="workspace-selection__title">
                     Workspace list
                     {!isLoading && hasWorkspaces && (
                        <span className="workspace-selection__count">
                           {workspaces.length}{" "}
                           {workspaces.length === 1 ? "workspace" : "workspaces"}
                        </span>
                     )}
                  </h2>
                  <p className="workspace-selection__subtitle">
                     Choose the workspace you would like to work on
                  </p>
               </div>

               {(onShowArchived || canCreateWorkspace) && (
                  <div className="workspace-selection__actions">
                     {onShowArchived && (
                        <button
                           type="button"
                           className="workspace-selection__archived"
                           onClick={onShowArchived}
                        >
                           <ArchiveBoxIcon />
                           Archived workspaces
                        </button>
                     )}
                     {canCreateWorkspace && (
                        <button
                           type="button"
                           className="workspace-selection__create"
                           onClick={() => setFormTarget({})}
                        >
                           <PlusOutlined />
                           New workspace
                        </button>
                     )}
                  </div>
               )}
            </div>

            <div className="workspace-selection__grid">
               {isLoading
                  ? SKELETON_CARDS.map((placeholder) => (
                       <div key={placeholder} className="workspace-card workspace-card--skeleton">
                          <span className="workspace-card__skeleton-mark" />
                          <span className="workspace-card__skeleton-line" />
                          <span className="workspace-card__skeleton-line workspace-card__skeleton-line--short" />
                       </div>
                    ))
                  : workspaces.map((workspace) => (
                       <WorkspaceCard
                          key={workspace.id}
                          workspace={workspace}
                          onOpen={() => onEnterWorkspace(workspace.id)}
                          actions={
                             renderWorkspaceActions
                                ? renderWorkspaceActions(workspace, {
                                     onEdit: () => setFormTarget({ workspace })
                                  })
                                : null
                          }
                       />
                    ))}

               {!isLoading && canCreateWorkspace && (
                  <button
                     type="button"
                     className="workspace-card workspace-card--new"
                     onClick={() => setFormTarget({})}
                  >
                     <span className="workspace-card__plus">
                        <PlusOutlined />
                     </span>
                     <span className="workspace-card__new-title">New workspace</span>
                  </button>
               )}
            </div>

            {!isLoading && !hasWorkspaces && !canCreateWorkspace && (
               <p className="workspace-selection__empty">
                  You don&apos;t have access to any workspace yet. Ask an administrator to add you
                  to one.
               </p>
            )}
         </section>

         <WorkspaceFormModal
            open={Boolean(formTarget)}
            workspace={formTarget?.workspace}
            onCancel={closeForm}
            onSaved={handleSaved}
         />
      </div>
   );
}

export default WorkspaceSelection;
